class ImageCenterLayout extends HTMLElement {
  constructor() {
    super();
    
    // Configuration
    this.autoRotateDelay = 5000; // 5 seconds between automatic changes
    this.transitionDuration = 300; // CSS transition duration in ms
    
    // State
    this.autoRotateTimer = null;
    this.isPaused = false;
    this.currentIndex = 0;
    
    // Elements
    this.options = [];
    this.titleElement = null;
    this.imageContainer = null;
    this.imageItems = [];
  }

  connectedCallback() {
    this.init();
  }

  disconnectedCallback() {
    this.stopAutoRotate();
    this.removeEventListeners();
  }

  init() {
    const slideDuration = parseInt(this.dataset.slideDuration) || 5;
    this.autoRotateDelay = slideDuration * 1000;
    
    // Get all radio inputs (options)
    this.options = Array.from(this.querySelectorAll('.image-center-layout__option-input'));
    
    // Get dynamic content elements
    this.titleElement = this.querySelector('[data-dynamic-title]');
    this.imageContainer = this.querySelector('[data-dynamic-image]');
    this.imageItems = Array.from(this.querySelectorAll('[data-image-index]'));
    
    if (!this.options.length) return;
    
    // Find initially checked option
    this.currentIndex = this.options.findIndex(option => option.checked);
    if (this.currentIndex === -1) this.currentIndex = 0;
    
    // Setup event listeners
    this.setupEventListeners();
    
    // Start auto-rotation
    this.startAutoRotate();
  }

  setupEventListeners() {
    this.options.forEach((option, index) => {
      option.addEventListener('change', () => this.handleOptionChange(index));
    });
    
    // Pause on hover or interaction
    this.addEventListener('mouseenter', () => this.pauseAutoRotate());
    this.addEventListener('mouseleave', () => this.resumeAutoRotate());
    
    // Pause on touch devices when interacting
    this.addEventListener('touchstart', () => this.pauseAutoRotate(), { passive: true });
    this.addEventListener('touchend', () => {
      setTimeout(() => this.resumeAutoRotate(), 3000); // Resume after 3s of no interaction
    }, { passive: true });
  }

  removeEventListeners() {
    this.options.forEach(option => {
      option.removeEventListener('change', this.handleOptionChange);
    });
  }

  handleOptionChange(index) {
    this.currentIndex = index;
    this.updateContent(index);
    this.resetAutoRotate();
  }

  updateContent(index) {
    const option = this.options[index];
    if (!option) return;
    
    const title = option.dataset.title;
    
    // Add fade-out class
    this.classList.add('image-center-layout--transitioning');
    
    setTimeout(() => {
      // Update title
      if (this.titleElement && title) {
        this.titleElement.innerHTML = title.replace(/\n/g, '<br>');
      }
      
      // Update active image - hide all, show current
      this.imageItems.forEach((item, itemIndex) => {
        if (itemIndex === index) {
          item.classList.add('is-active');
        } else {
          item.classList.remove('is-active');
        }
      });
      
      // Remove transition class
      setTimeout(() => {
        this.classList.remove('image-center-layout--transitioning');
      }, 50);
      
    }, this.transitionDuration / 2);
  }

  startAutoRotate() {
    this.stopAutoRotate();
    this.autoRotateTimer = setInterval(() => {
      if (!this.isPaused) {
        this.rotateToNext();
      }
    }, this.autoRotateDelay);
  }

  stopAutoRotate() {
    if (this.autoRotateTimer) {
      clearInterval(this.autoRotateTimer);
      this.autoRotateTimer = null;
    }
  }

  resetAutoRotate() {
    this.startAutoRotate();
  }

  pauseAutoRotate() {
    this.isPaused = true;
  }

  resumeAutoRotate() {
    this.isPaused = false;
  }

  rotateToNext() {
    // Calculate next index
    const nextIndex = (this.currentIndex + 1) % this.options.length;
    
    // Check the next option
    if (this.options[nextIndex]) {
      this.options[nextIndex].checked = true;
      this.handleOptionChange(nextIndex);
    }
  }
}

// Register custom element
if ('customElements' in window) {
  customElements.define('image-center-layout', ImageCenterLayout);
}

// Shopify Theme Editor support
if (Shopify?.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.querySelector('image-center-layout');
    if (section && section.init) {
      section.init();
    }
  });
  
  document.addEventListener('shopify:section:unload', (event) => {
    const section = event.target.querySelector('image-center-layout');
    if (section && section.disconnectedCallback) {
      section.disconnectedCallback();
    }
  });
}
